// AlbumImages.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { FaTimes, FaArrowLeft, FaArrowRight } from "react-icons/fa";

/* Grid for thumbnails */
const GridContainer = tw.div`
  grid
  grid-cols-2 sm:grid-cols-3 md:grid-cols-4
  gap-4
  p-4
`;

/* Thumbnail images */
const Thumbnail = styled.img`
  ${tw`w-full h-auto object-cover rounded cursor-pointer`}
`;

/* Modal overlay (dark background) */
const ModalOverlay = styled.div`
  ${tw`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75`}
`;

const ModalContent = styled.div`
  ${tw`relative flex flex-col items-center`}
  width: 75%;
`;

/* A container that wraps the large image so we can absolutely-position arrows. */
const ImageWrapper = tw.div`
  relative inline-block
`;

/* Enlarged image */
const LargeImage = styled.img`
  ${tw`max-w-full max-h-screen rounded`}
`;

/* Close button in top-right corner */
const CloseButton = styled.button`
  ${tw`absolute text-white hover:text-red-400 transition`}
  top: 1rem;
  right: 1rem;
  font-size: 2.5rem;
  z-index: 9999;
`;

/* Shared arrow styling */
const ArrowButton = styled.button`
  ${tw`absolute text-gray-100 hover:text-primary-400 transition p-2`}
  font-size: 3rem;
`;

/* Prev arrow => left of the image, vertically centered */
const PrevArrow = styled(ArrowButton)`
  left: 0;
  top: 50%;
  transform: translateY(-50%);
`;

/* Next arrow => right of the image, vertically centered */
const NextArrow = styled(ArrowButton)`
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;

/**
 * @param {Object} props
 * @param {string[]} props.images - Resim URL'lerinden oluşan bir dizi
 */
export default function AlbumImages({ images = [] }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // open modal at the clicked image index
  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  // close the modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // previous image
  const showPrevImage = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  // next image
  const showNextImage = () => {
    setCurrentIndex((prev) =>
      prev < images.length - 1 ? prev + 1 : prev
    );
  };

  // Close or navigate with keyboard: ESC => close, left => prev, right => next
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "ArrowLeft") {
        showPrevImage();
      } else if (e.key === "ArrowRight") {
        showNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  return (
    <>
      {/* Thumbnail grid */}
      <GridContainer>
        {images.map((imgSrc, idx) => (
          <Thumbnail
            key={idx}
            src={imgSrc}
            alt={`Fotoğraf-${idx}`}
            onClick={() => openModal(idx)}
          />
        ))}
      </GridContainer>

      {isModalOpen && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            {/* close button at top-right */}
            <CloseButton onClick={closeModal}>
              <FaTimes />
            </CloseButton>

            <ImageWrapper>
              {currentIndex > 0 && (
                <PrevArrow onClick={showPrevImage}>
                  <FaArrowLeft />
                </PrevArrow>
              )}

              <LargeImage
                src={images[currentIndex]}
                alt={`Büyük-Foto-${currentIndex}`}
              />

              {currentIndex < images.length - 1 && (
                <NextArrow onClick={showNextImage}>
                  <FaArrowRight />
                </NextArrow>
              )}
            </ImageWrapper>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
}
